// GlobalEventsContext.js
import React, { createContext, useContext, useEffect, useState } from "react";
import { apiFetchJson } from "apiClient";

const FRONTEND_BASE =
    process.env.REACT_APP_API_BASE || window.location.origin;

const GlobalEventsContext = createContext(null);

export const GlobalEventsProvider = ({ children }) => {
    // Per-component alert flags; keys are HEADER_TABS.componentKey (e.g. "SM", "PVA", "MON", etc.)
    const [activeAlertTabs, setActiveAlertTabs] = useState({});

    // --- SharpMonitoring state ---
    const [hasSharpMonitoringAlerts, setHasSharpMonitoringAlerts] =
        useState(false);
    const [sharpMonitoringVersion, setSharpMonitoringVersion] = useState(0);

    // --- Price/Volume alerts state (for global header + panels) ---
    const [priceVolumeAlerts, setPriceVolumeAlerts] = useState([]);
    const [hasPriceVolumeAlerts, setHasPriceVolumeAlerts] = useState(false);
    const [highlightedPriceVolumeAlertId, setHighlightedPriceVolumeAlertId] =
        useState(null);

    // --- Reminder state ---
    const [reminders, setReminders] = useState([]);
    const [hasReminders, setHasReminders] = useState(false);
    const [highlightedReminderId, setHighlightedReminderId] = useState(null);
    const [initialReminders, setInitialReminders] = useState([]);

    // ---------- SharpMonitoring helpers ----------

    const recomputeSharpMonitoringAlerts = async () => {
        try {
            const data = await apiFetchJson("/api/sharp-monitoring/stocks");
            const hasAlerts =
                Array.isArray(data) &&
                data.some(
                    (s) => Array.isArray(s.alerts) && s.alerts.length > 0
                );

            setHasSharpMonitoringAlerts(hasAlerts);
            setSharpMonitoringVersion((v) => v + 1);

            setActiveAlertTabs((prev) => ({
                ...prev,
                SM: hasAlerts, // "SM" is the componentKey for SHARPMONITORING
            }));
        } catch (e) {
            console.error("Failed to check sharp monitoring alerts", e);
        }
    };

    // --- INITIAL load (SharpMonitoring) ---
    useEffect(() => {
        recomputeSharpMonitoringAlerts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const loadExistingReminders = async () => {
            try {
                const data = await apiFetchJson("/api/reminders?onlyActive=true");
                const list = Array.isArray(data) ? data : [];

                setReminders(list);

                // find currently triggered & uncleared reminders
                const triggered = list.filter((r) => r.triggered && !r.cleared);
                const hasTriggered = triggered.length > 0;

                setHasReminders(hasTriggered);
                markReminderTab(hasTriggered);

                if (hasTriggered) {
                    // pick latest triggered to highlight
                    triggered.sort((a, b) => {
                        const ta = a.triggeredAt || a.createdAt || "";
                        const tb = b.triggeredAt || b.createdAt || "";
                        return tb.localeCompare(ta);
                    });
                    const latest = triggered[0];
                    if (latest && latest.id != null) {
                        setHighlightedReminderId(latest.id);
                    }
                }
            } catch (e) {
                console.error("Failed fetching existing reminders", e);
            }
        };

        loadExistingReminders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ------- INITIAL FETCH of already-triggered price-volume alerts -------
    useEffect(() => {
        const loadExistingPriceVolumeAlerts = async () => {
            try {
                const data = await apiFetchJson(
                    "/api/price-volume-alerts?onlyActive=true"
                );
                const list = Array.isArray(data) ? data : [];

                if (list.length > 0) {
                    setPriceVolumeAlerts(list);

                    // consider only triggered & not cleared alerts for flashing
                    const triggered = list.filter(a => a.triggered && !a.cleared);

                    const hasTriggered = triggered.length > 0;
                    setHasPriceVolumeAlerts(hasTriggered);

                    if (hasTriggered) {
                        setActiveAlertTabs((prev) => ({
                            ...prev,
                            PVA: true,
                        }));

                        // pick the latest triggered one to highlight
                        triggered.sort((a, b) => {
                            const ta = a.triggeredAt || a.createdAt || "";
                            const tb = b.triggeredAt || b.createdAt || "";
                            return tb.localeCompare(ta);
                        });

                        const latest = triggered[0];
                        if (latest && latest.id != null) {
                            setHighlightedPriceVolumeAlertId(latest.id);
                        }
                    }
                }

            } catch (e) {
                console.error(
                    "Failed fetching existing price-volume alerts",
                    e
                );
            }
        };

        loadExistingPriceVolumeAlerts();
    }, []);

    useEffect(() => {
        const loadExistingReminders = async () => {
            try {
                const data = await apiFetchJson("/api/reminders?onlyActive=true");
                const list = Array.isArray(data) ? data : [];

                setReminders(list);

                const triggered = list.filter(
                    r => r.triggered && !r.cleared
                );
                const hasTriggered = triggered.length > 0;
                setHasReminders(hasTriggered);

                if (hasTriggered) {
                    setActiveAlertTabs(prev => ({
                        ...prev,
                        REM: true,
                    }));

                    triggered.sort((a, b) => {
                        const ta = a.triggeredAt || a.createdAt || "";
                        const tb = b.triggeredAt || b.createdAt || "";
                        return tb.localeCompare(ta);
                    });
                    const latest = triggered[0];
                    if (latest && latest.id != null) {
                        setHighlightedReminderId(latest.id);
                    }
                }
            } catch (e) {
                console.error("Failed fetching existing reminders", e);
            }
        };

        loadExistingReminders();
    }, []);

    // ---------- Price/Volume alerts helpers (PVA header badge) ----------

    const markPVATab = (hasAny) => {
        setActiveAlertTabs((prev) => {
            const next = { ...prev };
            if (hasAny) {
                next.PVA = true; // "PVA" shows a PRICEVOLUMEALERTS badge in AppHeader
            } else {
                delete next.PVA;
            }
            return next;
        });
    };

    const clearPriceVolumeAlert = (alertId) => {
        setPriceVolumeAlerts((prev) => {
            // mark it cleared in local state (don't have to remove it)
            const next = prev.map(a =>
                a.id === alertId ? { ...a, cleared: true } : a
            );

            // only flash if there is any triggered AND not cleared
            const hasTriggered = next.some(a => a.triggered && !a.cleared);

            setHasPriceVolumeAlerts(hasTriggered);
            markPVATab(hasTriggered);

            if (alertId && alertId === highlightedPriceVolumeAlertId) {
                setHighlightedPriceVolumeAlertId(null);
            }

            return next;
        });
    };

    const clearHighlightedPriceVolumeAlert = () => {
        setHighlightedPriceVolumeAlertId(null);
    };

    // ------------------------------------------------------------
    // ✅ SINGLE unified SSE wiring for /events/stream
    // ------------------------------------------------------------
    useEffect(() => {
        let es;

        try {
            es = new EventSource(`${FRONTEND_BASE}/events/stream`, {
                withCredentials: false,
            });

            es.onopen = () => {
                console.log("Connected to unified SSE /events/stream");
            };

            // ---------------- SharpMonitoring events ----------------
            es.addEventListener("sharp-alert", () => {
                // We know there is at least one sharp alert now
                setHasSharpMonitoringAlerts(true);
                setSharpMonitoringVersion((v) => v + 1);
                setActiveAlertTabs((prev) => ({
                    ...prev,
                    SM: true,
                }));
            });

            es.addEventListener("sharp-alerts-changed", () => {
                // Recompute from backend (may clear SM from activeAlertTabs)
                recomputeSharpMonitoringAlerts();
            });

            // ---------------- Reminders events ----------------
            es.addEventListener("reminderTriggered", (event) => {
                try {
                    const payload = JSON.parse(event.data);

                    // Add & flash
                    setReminders(prev => {
                        const withoutOld = prev.filter(r => r.id !== payload.id);

                        const next = [
                            { ...payload, flash: true },   // 👈 mark flashing
                            ...withoutOld
                        ];

                        setHasReminders(true);
                        markReminderTab(true);

                        if (payload?.id != null) {
                            setHighlightedReminderId(payload.id);
                        }

                        return next;
                    });

                    // 🔥 Remove flash class after animation period
                    setTimeout(() => {
                        setReminders(prev =>
                            prev.map(r =>
                                r.id === payload.id ? { ...r, flash: false } : r
                            )
                        );
                    }, 4000); // Same timing as PVA

                } catch (e) {
                    console.error("Failed to parse reminderTriggered event", e);
                }
            });

            es.addEventListener("remindersChanged", async () => {
                try {
                    const data = await apiFetchJson("/api/reminders?onlyActive=true");
                    const list = Array.isArray(data) ? data : [];
                    setReminders(list);

                    const hasTriggered = list.some(
                        r => r.triggered && !r.cleared
                    );
                    setHasReminders(hasTriggered);
                    markReminderTab(hasTriggered);
                } catch (e) {
                    console.error("Failed to recompute reminders", e);
                }
            });

            // ---------------- Price/Volume alerts events ----------------
            es.addEventListener("priceVolumeAlert", (event) => {
                try {
                    const payload = JSON.parse(event.data);

                    // prepend newest alert
                    setPriceVolumeAlerts((prev) => {
                        const next = [payload, ...prev];
                        setHasPriceVolumeAlerts(true);
                        // mark header badge
                        markPVATab(true);
                        // remember which alert should flash in manager
                        if (payload && payload.id != null) {
                            setHighlightedPriceVolumeAlertId(payload.id);
                        }
                        return next;
                    });
                } catch (e) {
                    console.error(
                        "Failed to parse priceVolumeAlert event",
                        e
                    );
                }
            });

            es.onerror = (err) => {
                console.error("SSE error (/events/stream)", err);
            };
        } catch (e) {
            console.error(
                "Error setting up unified SSE /events/stream in GlobalEventsProvider",
                e
            );
        }

        return () => {
            if (es) es.close();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const markReminderTab = (hasAny) => {
        setActiveAlertTabs((prev) => {
            const next = { ...prev };
            if (hasAny) {
                next.REM = true;
            } else {
                delete next.REM;
            }
            return next;
        });
    };

    const markReminderClearedInContext = (remId) => {
        setReminders(prev => {
            const next = prev.map(r =>
                r.id === remId ? { ...r, cleared: true } : r
            );
            const hasTriggered = next.some(r => r.triggered && !r.cleared);
            setHasReminders(hasTriggered);
            markReminderTab(hasTriggered);
            return next;
        });

        if (remId && remId === highlightedReminderId) {
            setHighlightedReminderId(null);
        }
    };

    const clearHighlightedReminder = () => {
        setHighlightedReminderId(null);
    };

    const value = {
        // generic
        activeAlertTabs,

        // Sharp monitoring
        hasSharpMonitoringAlerts,
        sharpMonitoringVersion,

        // Price/Volume alerts
        priceVolumeAlerts,
        hasPriceVolumeAlerts,
        clearPriceVolumeAlert,
        highlightedPriceVolumeAlertId,
        clearHighlightedPriceVolumeAlert,

        reminders,
        hasReminders,
        highlightedReminderId,
        clearHighlightedReminder,
        markReminderClearedInContext,
    };

    return (
        <GlobalEventsContext.Provider value={value}>
            {children}
        </GlobalEventsContext.Provider>
    );
};

export const useGlobalEvents = () => {
    const ctx = useContext(GlobalEventsContext);
    if (!ctx) {
        throw new Error("useGlobalEvents must be used inside GlobalEventsProvider");
    }
    return ctx;
};
